import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface Product {
  id: number;
  name: string;
  description: string;
  mystery_price: number;
  original_price: number;
  mystery_type: string;
  category: { name: string };
  vendor: { name: string };
}

interface UserStatus {
  daily_swipes_used: number;
  daily_swipes_remaining: number;
  is_vip: boolean;
  has_seen_all_products: boolean;
  cart_product_ids: number[];
  is_new_day: boolean;
}

interface FeedStatus {
  has_more_products: boolean;
  message: string;
  show_load_more: boolean;
  show_vip_upgrade: boolean;
  total_available_products: number;
  products_seen: number;
}

interface SmartFeedResponse {
  success: boolean;
  products: Product[];
  user_status: UserStatus;
  feed_status: FeedStatus;
}

export default function SmartFeedTest() {
  const { token, user } = useAuth();
  const [feedData, setFeedData] = useState<SmartFeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testScenario, setTestScenario] = useState<string>('current');

  const loadSmartFeed = async (action: 'initial_load' | 'load_more' | 'refresh' = 'initial_load') => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/products/smart_feed/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({ action })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFeedData(data);
        console.log('Smart Feed Response:', data);
      } else {
        setError(data.error || 'Failed to load smart feed');
      }
    } catch (err) {
      setError('Network error: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const applyTestScenario = async (scenario: string) => {
    // DISABLED: Test scenario functionality removed to prevent data corruption
    setError('⚠️ Test scenario functionality has been disabled to protect user data. This is now a read-only test interface.');
    return;
  };

  useEffect(() => {
    loadSmartFeed('initial_load');
  }, [token]);

  if (!user) {
    return <div className="p-8 text-center">Please log in to test the smart feed</div>;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🧠 Smart Feed API Test</h1>
        
        {/* Scenario Testing */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">🎭 Test Different Scenarios</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <button
              onClick={() => {
                setTestScenario('current');
                loadSmartFeed('initial_load');
              }}
              disabled={loading}
              className={`p-3 rounded-lg font-semibold transition-colors ${
                testScenario === 'current' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              🔍 Current State
            </button>
            <button
              onClick={() => {
                setTestScenario('new_user');
                applyTestScenario('new_user');
              }}
              disabled={loading}
              className={`p-3 rounded-lg font-semibold transition-colors ${
                testScenario === 'new_user' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              👤 New User
            </button>
            <button
              onClick={() => {
                setTestScenario('daily_limit');
                applyTestScenario('daily_limit');
              }}
              disabled={loading}
              className={`p-3 rounded-lg font-semibold transition-colors ${
                testScenario === 'daily_limit' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              🚫 Daily Limit
            </button>
            <button
              onClick={() => {
                setTestScenario('vip_user');
                applyTestScenario('vip_user');
              }}
              disabled={loading}
              className={`p-3 rounded-lg font-semibold transition-colors ${
                testScenario === 'vip_user' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              👑 VIP User
            </button>
          </div>
          
          <div className="text-sm text-gray-400 mb-4">
            {testScenario === 'current' && "🔍 Testing with your current user state"}
            {testScenario === 'new_user' && "👤 Simulating a new user with no swipes or cart items"}
            {testScenario === 'daily_limit' && "🚫 Simulating a user who has reached daily swipe limit"}
            {testScenario === 'vip_user' && "👑 Simulating a VIP user with unlimited swipes"}
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => applyTestScenario('reset')}
              disabled={loading}
              className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-semibold text-sm"
            >
              🔄 Reset to Original State
            </button>
            <div className="text-xs text-gray-500 flex items-center">
              ⚠️ Warning: This will modify your actual user data for testing
            </div>
          </div>
        </div>
        
        {/* User Status */}
        {feedData && (
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">👤 User Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <span className="text-gray-400">Daily Swipes Used:</span>
                <div className="text-2xl font-bold text-blue-400">{feedData.user_status.daily_swipes_used}</div>
              </div>
              <div>
                <span className="text-gray-400">Daily Swipes Remaining:</span>
                <div className="text-2xl font-bold text-green-400">{feedData.user_status.daily_swipes_remaining}</div>
              </div>
              <div>
                <span className="text-gray-400">VIP Status:</span>
                <div className="text-2xl font-bold text-purple-400">
                  {feedData.user_status.is_vip ? '👑 VIP' : '👤 Regular'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">New Day:</span>
                <div className="text-2xl font-bold text-yellow-400">
                  {feedData.user_status.is_new_day ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Cart Items:</span>
                <div className="text-2xl font-bold text-orange-400">{feedData.user_status.cart_product_ids.length}</div>
              </div>
              <div>
                <span className="text-gray-400">Seen All Products:</span>
                <div className="text-2xl font-bold text-red-400">
                  {feedData.user_status.has_seen_all_products ? '✅ Yes' : '❌ No'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feed Status */}
        {feedData && (
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">📊 Feed Status</h2>
            <div className="mb-4">
              <span className="text-gray-400">Message:</span>
              <div className="text-lg text-white mt-1">{feedData.feed_status.message}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-gray-400">Has More Products:</span>
                <div className="text-xl font-bold text-green-400">
                  {feedData.feed_status.has_more_products ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Show Load More:</span>
                <div className="text-xl font-bold text-blue-400">
                  {feedData.feed_status.show_load_more ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Show VIP Upgrade:</span>
                <div className="text-xl font-bold text-purple-400">
                  {feedData.feed_status.show_vip_upgrade ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div>
                <span className="text-gray-400">Total Available:</span>
                <div className="text-xl font-bold text-yellow-400">{feedData.feed_status.total_available_products}</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => loadSmartFeed('initial_load')}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-6 py-3 rounded-lg font-semibold"
          >
            {loading ? 'Loading...' : '🔄 Initial Load'}
          </button>
          <button
            onClick={() => loadSmartFeed('load_more')}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-6 py-3 rounded-lg font-semibold"
          >
            {loading ? 'Loading...' : '➕ Load More'}
          </button>
          <button
            onClick={() => loadSmartFeed('refresh')}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 px-6 py-3 rounded-lg font-semibold"
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-800 border border-red-600 rounded-lg p-4 mb-6">
            <h3 className="text-red-200 font-semibold">❌ Error</h3>
            <p className="text-red-100">{error}</p>
          </div>
        )}

        {/* Products Display */}
        {feedData && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">
              🛍️ Products ({feedData.products.length})
            </h2>
            
            {feedData.products.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-2xl font-bold mb-2">Ready for More?</h3>
                <p className="text-lg">{feedData.feed_status.message}</p>
                
                <div className="mt-6 space-y-3">
                  {feedData.feed_status.show_load_more && (
                    <button
                      onClick={() => loadSmartFeed('load_more')}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                    >
                      <span>🔄</span>
                      Load More Products
                    </button>
                  )}
                  
                  {feedData.feed_status.show_vip_upgrade && (
                    <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2">
                      <span>👑</span>
                      Upgrade to VIP for Unlimited Swipes
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {feedData.products.map((product) => (
                  <div key={product.id} className="bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                    <p className="text-gray-300 text-sm mb-3">{product.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-400">Price:</span>
                        <span className="text-green-400 font-semibold ml-2">₹{product.mystery_price}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Original:</span>
                        <span className="text-gray-300 ml-2">₹{product.original_price}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Type:</span>
                        <span className="text-blue-400 ml-2">{product.mystery_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Category:</span>
                        <span className="text-yellow-400 ml-2">{product.category.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Vendor:</span>
                        <span className="text-purple-400 ml-2">{product.vendor.name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
